import { Component, computed, inject, model, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgScrollbar } from 'ngx-scrollbar';
import { Store } from '@ngrx/store';
import { FloatingInputComponent } from '../../../../inputs/floating-input/floating-input.component';
import { AddRemoveButtonsComponent } from '../../../../buttons/add-remove-buttons/add-remove-buttons.component';
import { SimpleButtonComponent } from '../../../../buttons/simple-medium-button/simple-button.component';
import { DropdownComponent } from '../../../../inputs/dropdown/dropdown.component';
import {
  getDefaultMarkup,
  getDefaultMarkupGroup,
} from '@app/store/prime-commitments/prime-commitments.constants';
import {
  PrimeContractModel,
  MarkupBaseOption,
  MarkupGroup,
  PrimeDescriptionOption,
} from '@app/store/prime-commitments/prime-commitments.types';
import { CommitmentEntryTextComponent } from '@app/framework/commitments/sidebar/commitment-entry-text/commitment-entry-text.component';
import { primeContractActions } from '@app/store/prime-commitments/prime-commitments.actions';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';

@Component({
  selector: 'app-prime-change-order-markups',
  imports: [
    FloatingInputComponent,
    AddRemoveButtonsComponent,
    SimpleButtonComponent,
    DropdownComponent,
    FormsModule,
    NgScrollbar,
    CommitmentEntryTextComponent,
    SidebarHeaderComponent,
  ],
  templateUrl: './prime-change-order-markups.component.html',
  styleUrl: './prime-change-order-markups.component.scss',
})
export class PrimeChangeOrderMarkupsComponent implements OnInit {
  private readonly store = inject(Store);

  @ViewChild('COMarkups') markupsForm: NgForm;

  model = model<PrimeContractModel>();

  allScheduleOfValues = signal<PrimeDescriptionOption[]>([]);

  // map of group index to dropdown options
  markupBaseOptions = signal<MarkupBaseOption[][]>([]);
  disabledMarkupBaseOptionIds = computed(() => {
    const selectedIds = new Set<number>();
    this.model().markupGroups.forEach((group) => {
      if (group.selectedOptionId !== undefined && group.selectedOptionId !== null) {
        selectedIds.add(group.selectedOptionId);
      }
    });
    return selectedIds;
  });

  disabledSOVIds = computed<Set<string>>(() => {
    const disabledIDs = this.model().markupGroups.flatMap((group) =>
      group.markups.map((markup) => markup.sov_id),
    );
    return new Set(disabledIDs);
  });

  ngOnInit() {
    const allSOVs = this.model().sovs.map((entry) => {
      return {
        description_id: entry.id,
        name: entry.name,
      };
    });
    this.allScheduleOfValues.set(allSOVs);

    const markupGroups = this.model().markupGroups;
    markupGroups.forEach((markupGroup, index) => {
      const markupOptions = this.getMarkupBaseOptions(index);
      this.markupBaseOptions.update((value) => {
        return value.toSpliced(index, 0, markupOptions);
      });

      // reverse engineer the selected option based on base_group_indexes
      // e.g. if base_group_indexes = [0, 2], set the selectedOptionId to 3.
      const selectedOption = markupOptions.find((option) => {
        return (
          option.combinations.length === markupGroup.base_group_indexes.length &&
          option.combinations.every((optionId) => markupGroup.base_group_indexes.includes(optionId))
        );
      });
      if (selectedOption) {
        markupGroup.selectedOptionId = selectedOption.id;
      }
    });

    this.model.update((value) => ({
      ...value,
      markupGroups,
    }));
  }

  addGroup() {
    let markupGroups = this.model().markupGroups;
    markupGroups.push(getDefaultMarkupGroup());
    markupGroups = markupGroups.map((item, index) => {
      return {
        ...item,
        index: index + 1, // todo: why +1? other indexes are 0-based
      };
    });

    this.markupBaseOptions.update((value) => {
      return [...value, this.getMarkupBaseOptions(markupGroups.length - 1)];
    });

    this.model.update((value) => ({
      ...value,
      markupGroups,
    }));
  }

  removeGroup(groupIndex: number) {
    let markupGroups = this.model().markupGroups;
    markupGroups.splice(groupIndex, 1);
    markupGroups = markupGroups.map((markupGroup, index) => {
      return {
        ...markupGroup,
        // reset selectedOptionId if the group is after the removed one (they could depend on it)
        selectedOptionId: markupGroup.index < groupIndex + 1 ? markupGroup.selectedOptionId : null,
        index: index + 1,
      };
    });

    const newOptions = markupGroups.map((_, index) => {
      return this.getMarkupBaseOptions(index);
    });
    this.markupBaseOptions.set(newOptions);

    this.model.update((value) => ({
      ...value,
      markupGroups,
    }));
  }

  selectMarkupCombination(optionId: number, group: MarkupGroup, groupIndex: number) {
    const base_group_indexes = this.markupBaseOptions()[groupIndex].find(
      (option: MarkupBaseOption) => option.id === optionId,
    ).combinations;

    const markupGroups = this.model().markupGroups.map((group, index) => {
      if (index !== groupIndex) return group;
      return {
        ...group,
        base_group_indexes,
        selectedOptionId: optionId,
      };
    });

    this.model.update((value) => ({
      ...value,
      markupGroups,
    }));
  }

  private getMarkupBaseOptions(groupIndex: number): MarkupBaseOption[] {
    return this.getValidCombinations(groupIndex + 1).flatMap((combinations, i) => {
      const label = combinations
        .map((groupIndex) => {
          if (groupIndex === 0) {
            return 'Subtotal';
          }
          return 'Group ' + groupIndex;
        })
        .join(' + ');
      return { id: i, label, combinations };
    });
  }

  /**
   * Generate all valid combinations.
   * Ensures that subtotal (0) is always included.
   * Excludes the full subset [0, 1, 2, 3].
   * @return {number[][]} Array of valid group combinations.
   */
  private getValidCombinations(totalGroups: number): number[][] {
    const subtotal = 0; // this is always included
    const groupIndexes = Array.from({ length: totalGroups }, (_, i) => i + 1);
    const combinations: number[][] = [];

    // Iterate over all possible subsets (excluding empty and full set), 2^N - 1 (111) is skipped to avoid selecting all groups.
    // Ex: Math.pow(2, 3) = 8, Binary range: 000 → 111 (0 to 7)
    for (let subsetMask = 0; subsetMask < Math.pow(2, totalGroups) - 1; subsetMask++) {
      const combination = [subtotal]; // Always include subtotal (0)

      for (let index = 0; index < totalGroups; index++) {
        // checks if the bit at position index is set (1) and group should be included or not
        // ex: 001 => [1], 110 => [3, 2], 011, [2,1], etc
        if (subsetMask & Math.pow(2, index)) {
          combination.push(groupIndexes[index]);
        }
      }

      if (!combination.includes(totalGroups)) {
        combinations.push(combination);
      }
    }

    return combinations.sort((a, b) => a.length - b.length);
  }

  removeItem(index: number, markupIndex: number) {
    const markupGroups = this.model().markupGroups;
    markupGroups[index].markups.splice(markupIndex, 1);
    markupGroups[index].markups = markupGroups[index].markups.map((item, index) => {
      return {
        ...item,
        index,
      };
    });

    this.model.update((value) => ({
      ...value,
      markupGroups,
    }));
  }

  addItem(index: number, markupIndex: number) {
    const markupGroups = this.model().markupGroups;

    markupGroups[index].markups.splice(markupIndex + 1, 0, getDefaultMarkup());
    markupGroups[index].markups = markupGroups[index].markups.map((item, index) => {
      return {
        ...item,
        index,
      };
    });

    this.model.update((value) => ({
      ...value,
      markupGroups,
    }));
  }

  isFormValid() {
    this.markupsForm.form.markAllAsTouched();
    return this.markupsForm.form.valid;
  }

  goToNextPage() {
    this.markupsForm.form.markAllAsTouched();
    if (!this.isFormValid()) {
      return;
    }
    this.store.dispatch(primeContractActions.onPrimeContractClickNext());
  }

  goBack() {
    this.store.dispatch(primeContractActions.onPrimeContactPreviousPage());
  }

  updateMarkupSovId(groupIndex: number, markupIndex: number, $event: string) {
    this.model.update((modelValue) => {
      const markupGroups = modelValue.markupGroups.map((markupGroup, gIndex) => {
        if (gIndex !== groupIndex) return markupGroup;

        const markups = markupGroup.markups.map((markup, mIndex) => {
          if (mIndex !== markupIndex) return markup;
          return {
            ...markup,
            sov_id: $event,
          };
        });

        return {
          ...markupGroup,
          markups,
        };
      });

      return {
        ...modelValue,
        markupGroups,
      };
    });
  }
}
